/**
 * Dashboard API Client
 *
 * Typed API client for document dashboard endpoints with automatic error handling.
 * Provides methods for document listing, folder management, and document operations.
 *
 * @module features/dashboard/api/dashboardApiClient
 * @see SPEC-016 Requirements 1.1, 2.1, 2.4, 7.4
 */

import { ApiError, ApiErrorCode } from '@linkode/shared';
import type {
  ListDocsQueryInput,
  ListDocsResponseInput,
  CreateFolderRequestInput,
  CreateFolderResponseInput,
  RenameFolderRequestInput,
  RenameFolderResponseInput,
  DeleteFolderResponseInput,
  MoveDocRequestInput,
  MoveDocResponseInput,
  DeleteDocResponseInput,
} from '@linkode/shared';
import { useAuthStore } from '../../auth/store/authStore';

// ============================================================================
// Constants
// ============================================================================

/** API base URL from environment variable */
const API_BASE_URL =
  import.meta.env.VITE_API_URL ||
  (import.meta.env.DEV ? 'http://localhost:8787' : 'https://api-staging.getlinkode.com');

// ============================================================================
// API Client Interface
// ============================================================================

/**
 * Dashboard API Client Interface
 *
 * Defines the contract for dashboard API operations.
 *
 * @see Requirements 1.1, 2.1, 2.4, 7.4
 */
export interface DashboardApiClient {
  /**
   * Create a new document
   *
   * @param title - Optional document title (default: 'Untitled')
   * @param initialContent - Optional initial markdown content
   * @returns Promise resolving to created document info with docId
   * @throws ApiError on failure
   * @see SPEC-016 Requirement 9.2
   */
  createDoc(title?: string, initialContent?: string): Promise<{ docId: string }>;

  /**
   * List documents for the current user with optional filtering and pagination
   *
   * @param query - Query parameters for filtering, sorting, and pagination
   * @returns Promise resolving to paginated document list
   * @throws ApiError on failure
   * @see Requirement 1.1
   */
  listDocs(query?: Partial<ListDocsQueryInput>): Promise<ListDocsResponseInput>;

  /**
   * Create a new folder
   *
   * @param request - Folder creation request with name and parent path
   * @returns Promise resolving to created folder info
   * @throws ApiError on failure
   * @see Requirement 2.1
   */
  createFolder(request: CreateFolderRequestInput): Promise<CreateFolderResponseInput>;

  /**
   * Rename an existing folder
   *
   * @param folderId - ID of the folder to rename
   * @param request - Rename request with new name
   * @returns Promise resolving to rename result
   * @throws ApiError on failure
   * @see Requirement 2.2
   */
  renameFolder(
    folderId: string,
    request: RenameFolderRequestInput
  ): Promise<RenameFolderResponseInput>;

  /**
   * Delete a folder (moves contained documents to parent)
   *
   * @param folderId - ID of the folder to delete
   * @returns Promise resolving to delete result
   * @throws ApiError on failure
   * @see Requirement 2.3
   */
  deleteFolder(folderId: string): Promise<DeleteFolderResponseInput>;

  /**
   * Move a document to a different folder
   *
   * @param docId - ID of the document to move
   * @param request - Move request with target folder path
   * @returns Promise resolving to move result
   * @throws ApiError on failure
   * @see Requirement 2.4
   */
  moveDoc(docId: string, request: MoveDocRequestInput): Promise<MoveDocResponseInput>;

  /**
   * Soft delete a document (moves to trash, recoverable for 30 days)
   *
   * @param docId - ID of the document to delete
   * @returns Promise resolving to delete result
   * @throws ApiError on failure
   * @see Requirement 7.4
   */
  deleteDoc(docId: string): Promise<DeleteDocResponseInput>;

  /**
   * Duplicate a document with "(Copy)" suffix
   *
   * @param docId - ID of the document to duplicate
   * @returns Promise resolving to the new document info
   * @throws ApiError on failure
   * @see Requirement 7.5
   */
  duplicateDoc(docId: string): Promise<{ docId: string; title: string }>;
}

// ============================================================================
// API Client Implementation
// ============================================================================

/**
 * Dashboard API Client Implementation
 *
 * Handles all dashboard-related API calls with:
 * - Automatic Authorization header injection
 * - Unified error handling
 * - UNAUTHORIZED auto-logout
 *
 * @see Requirements 1.1, 2.1, 2.4, 7.4
 */
class DashboardApiClientImpl implements DashboardApiClient {
  /**
   * Get Authorization header if session token exists
   *
   * @returns Headers object with Authorization if authenticated
   */
  private getAuthHeader(): HeadersInit {
    const token = useAuthStore.getState().sessionToken;
    return token ? { Authorization: `Bearer ${token}` } : {};
  }

  /**
   * Handle API response with unified error handling
   *
   * @param response - Fetch Response object
   * @returns Parsed JSON response
   * @throws ApiError on non-OK response
   */
  private async handleResponse<T>(response: Response): Promise<T> {
    if (!response.ok) {
      let error: ApiError;

      try {
        error = (await response.json()) as ApiError;
      } catch {
        // If response is not valid JSON, create a generic error
        error = {
          code: ApiErrorCode.INTERNAL_ERROR,
          message: 'An unexpected error occurred',
          requestId: 'unknown',
        };
      }

      // Note: UNAUTHORIZED handling is done by dashboardStore to prevent
      // React Error #185 (Maximum update depth exceeded). The store will
      // skip setting error state for UNAUTHORIZED, and AuthGuard will
      // handle the redirect via its own auth check mechanism.

      throw error;
    }

    return response.json();
  }

  /**
   * Build query string from parameters
   *
   * @param params - Query parameters object
   * @returns URL query string
   */
  private buildQueryString(params: Record<string, unknown>): string {
    const searchParams = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined && value !== null && value !== '') {
        searchParams.append(key, String(value));
      }
    }
    const queryString = searchParams.toString();
    return queryString ? `?${queryString}` : '';
  }

  /**
   * Create a new document
   *
   * @see SPEC-016 Requirement 9.2
   */
  async createDoc(title?: string, initialContent?: string): Promise<{ docId: string }> {
    const response = await fetch(`${API_BASE_URL}/docs`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...this.getAuthHeader(),
      },
      body: JSON.stringify({
        title: title ?? 'Untitled',
        initialContent: initialContent ?? '',
      }),
    });

    return this.handleResponse<{ docId: string }>(response);
  }

  /**
   * List documents for the current user
   *
   * @see Requirement 1.1
   */
  async listDocs(query?: Partial<ListDocsQueryInput>): Promise<ListDocsResponseInput> {
    const queryString = this.buildQueryString(query ?? {});
    const response = await fetch(`${API_BASE_URL}/dashboard/list${queryString}`, {
      method: 'GET',
      headers: {
        ...this.getAuthHeader(),
      },
    });

    return this.handleResponse<ListDocsResponseInput>(response);
  }

  /**
   * Create a new folder
   *
   * @see Requirement 2.1
   */
  async createFolder(request: CreateFolderRequestInput): Promise<CreateFolderResponseInput> {
    const response = await fetch(`${API_BASE_URL}/dashboard/folders`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...this.getAuthHeader(),
      },
      body: JSON.stringify(request),
    });

    return this.handleResponse<CreateFolderResponseInput>(response);
  }

  /**
   * Rename an existing folder
   *
   * @see Requirement 2.2
   */
  async renameFolder(
    folderId: string,
    request: RenameFolderRequestInput
  ): Promise<RenameFolderResponseInput> {
    const response = await fetch(`${API_BASE_URL}/dashboard/folders/${folderId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...this.getAuthHeader(),
      },
      body: JSON.stringify(request),
    });

    return this.handleResponse<RenameFolderResponseInput>(response);
  }

  /**
   * Delete a folder
   *
   * @see Requirement 2.3
   */
  async deleteFolder(folderId: string): Promise<DeleteFolderResponseInput> {
    const response = await fetch(`${API_BASE_URL}/dashboard/folders/${folderId}`, {
      method: 'DELETE',
      headers: {
        ...this.getAuthHeader(),
      },
    });

    return this.handleResponse<DeleteFolderResponseInput>(response);
  }

  /**
   * Move a document to a different folder
   *
   * @see Requirement 2.4
   */
  async moveDoc(docId: string, request: MoveDocRequestInput): Promise<MoveDocResponseInput> {
    const response = await fetch(`${API_BASE_URL}/dashboard/docs/${docId}/move`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...this.getAuthHeader(),
      },
      body: JSON.stringify(request),
    });

    return this.handleResponse<MoveDocResponseInput>(response);
  }

  /**
   * Soft delete a document
   *
   * @see Requirement 7.4
   */
  async deleteDoc(docId: string): Promise<DeleteDocResponseInput> {
    const response = await fetch(`${API_BASE_URL}/dashboard/docs/${docId}`, {
      method: 'DELETE',
      headers: {
        ...this.getAuthHeader(),
      },
    });

    return this.handleResponse<DeleteDocResponseInput>(response);
  }

  /**
   * Duplicate a document
   *
   * @see Requirement 7.5
   */
  async duplicateDoc(docId: string): Promise<{ docId: string; title: string }> {
    const response = await fetch(`${API_BASE_URL}/dashboard/docs/${docId}/duplicate`, {
      method: 'POST',
      headers: {
        ...this.getAuthHeader(),
      },
    });

    return this.handleResponse<{ docId: string; title: string }>(response);
  }
}

// ============================================================================
// Singleton Export
// ============================================================================

/**
 * Singleton instance of Dashboard API Client
 *
 * @example
 * ```typescript
 * import { dashboardApiClient } from '../api/dashboardApiClient';
 *
 * // List documents
 * const { docs, total, hasMore } = await dashboardApiClient.listDocs({
 *   sortBy: 'updatedAt',
 *   sortOrder: 'desc',
 *   limit: 50,
 * });
 *
 * // Create folder
 * const { folderId, path } = await dashboardApiClient.createFolder({
 *   name: 'My Folder',
 *   parentPath: '',
 * });
 *
 * // Move document
 * await dashboardApiClient.moveDoc(docId, { targetFolderPath: '/projects' });
 *
 * // Delete document
 * await dashboardApiClient.deleteDoc(docId);
 * ```
 */
export const dashboardApiClient: DashboardApiClient = new DashboardApiClientImpl();
